import { useEffect, useRef } from "react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

import { Button } from "@/components/Button";

/** Was das Detail-Fenster zu einem angeklickten Kalendertermin zeigt. */
export interface EventDetail {
  name: string;
  roomName?: string;
  start: Date;
  end: Date;
  sourceLabel?: string | null;
  sourceUrl?: string | null;
}

interface EventDetailDialogProps {
  detail: EventDetail | null;
  onClose: () => void;
}

/** Natives `<dialog>`: Fokus-Falle, Esc und Backdrop liefert der Browser. */
export function EventDetailDialog({ detail, onClose }: EventDetailDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // ponytail: jsdom kennt showModal() nicht — dort bleibt das Fenster zu.
    if (detail && !dialog.open) dialog.showModal?.();
    if (!detail && dialog.open) dialog.close();
  }, [detail]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      // Klick auf den abgedunkelten Hintergrund schließt, Klick in den Inhalt nicht.
      onClick={(e) => e.target === ref.current && onClose()}
      className="w-[min(92vw,420px)] rounded-card bg-surface p-0 shadow-card backdrop:bg-black/40"
    >
      {detail && (
        <div className="p-5">
          <h3 className="font-display text-h3 text-ink">{detail.name}</h3>
          <dl className="mt-4 space-y-2 text-body-sm">
            <div>
              <dt className="text-ink-muted">Wann</dt>
              <dd className="text-ink">
                {format(detail.start, "EEEE, d. MMMM yyyy", { locale: de })} ·{" "}
                {format(detail.start, "HH:mm")}–{format(detail.end, "HH:mm")} Uhr
              </dd>
            </div>
            {detail.roomName && (
              <div>
                <dt className="text-ink-muted">Raum</dt>
                <dd className="text-ink">{detail.roomName}</dd>
              </div>
            )}
            {detail.sourceLabel && (
              <div>
                <dt className="text-ink-muted">Quelle</dt>
                <dd className="text-ink">
                  {detail.sourceUrl ? (
                    <a
                      href={detail.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      {detail.sourceLabel}
                    </a>
                  ) : (
                    detail.sourceLabel
                  )}
                </dd>
              </div>
            )}
          </dl>
          <div className="mt-5 flex justify-end">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Schließen
            </Button>
          </div>
        </div>
      )}
    </dialog>
  );
}
